import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../../lib/api/axios';
import { FilterAggregations } from 'src/features/chat/types';

export interface UseAggregationsOptions {
  search?: string;
  categories?: string[];
  domains?: string[];
  subdomains?: string[];
  authorities?: string[];
  documentIds?: string[];
  scraperIds?: string[];
  favouritesOnly?: boolean;
  sources?: string[];
  restrictToManaged?: boolean;
  publicOnly?: boolean;
  enabled?: boolean;
}

/**
 * Fetches filter aggregations (category/domain/subdomain counts) from the backend.
 * These counts are used to populate the filter sidebar.
 */
export const useAggregations = (options: UseAggregationsOptions = {}) => {
  const {
    search = '',
    categories = [],
    domains = [],
    subdomains = [],
    authorities = [],
    documentIds = [],
    scraperIds = [],
    favouritesOnly = false,
    sources = [],
    restrictToManaged = false,
    publicOnly = false,
    enabled = true
  } = options;

  const [debouncedSearch, setDebouncedSearch] = useState(search);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Build query params
  const params = new URLSearchParams();
  if (debouncedSearch) {
    params.set('search', debouncedSearch);
  }
  // Add filter parameters
  categories.forEach((cat) => params.append('categories', cat));
  domains.forEach((dom) => params.append('domains', dom));
  subdomains.forEach((sub) => params.append('subdomains', sub));
  authorities.forEach((authority) => params.append('authorities', authority.toUpperCase()));
  documentIds.forEach((documentId) => params.append('document_ids', documentId));
  scraperIds.forEach((scraperId) => params.append('scraper_ids', scraperId));
  sources.forEach((src) => params.append('sources', src));
  if (favouritesOnly) {
    params.set('favourites_only', 'true');
  }
  if (restrictToManaged) {
    params.set('restrict_to_managed', 'true');
  }
  if (publicOnly) {
    params.set('public_only', 'true');
  }

  const endpoint = `/docs/filter-counts?${params.toString()}`;

  return useQuery({
    enabled,
    queryKey: [
      'aggregations',
      debouncedSearch,
      categories,
      domains,
      subdomains,
      authorities,
      documentIds,
      scraperIds,
      favouritesOnly,
      sources,
      restrictToManaged,
      publicOnly
    ],
    queryFn: async (): Promise<FilterAggregations> => {
      const response = await apiGet<FilterAggregations>(endpoint, {
        meta: {
          globalError: true,
          context: 'aggregations-fetch',
          description:
            'We failed to fetch aggregations from our database. Please contact support if the problem persists.',
          title: 'Failed to load aggregations.'
        }
      });
      return response.data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData
  });
};
